import React, { useState } from "react";
import "./Quote.css";

const Quote = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    eventDate: "",
    guestCount: "",
    location: "",
    budget: "",
    specialRequests: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/send-quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit quote request");
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        eventDate: "",
        guestCount: "",
        location: "",
        budget: "",
        specialRequests: "",
      });
    } catch (error) {
      console.error("Error submitting quote:", error);
      setIsSubmitting(false);
      alert("Failed to submit quote request. Please try again.");
    }
  };

  if (isSubmitted) {
    return (
      <div className="quote-page">
        <div className="container">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>
              Your quote request has been submitted successfully. We'll get back
              to you within 24 hours with a personalized quote for your event.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setIsSubmitted(false)}
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quote-page">
      <div className="container">
        <div className="quote-header">
          <h1>Get Your Custom Quote</h1>
          <p>
            Tell us about your event and we'll provide you with a personalized
            quote for our bartending services.
          </p>
        </div>

        <div className="quote-content">
          <div className="quote-info">
            <h2>What We Need to Know</h2>
            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon">📅</div>
                <h3>Event Details</h3>
                <p>
                  Date, time, and type of event to tailor our services
                  perfectly.
                </p>
              </div>
              <div className="info-item">
                <div className="info-icon">👥</div>
                <h3>Guest Count</h3>
                <p>
                  Number of guests to determine staffing and equipment needs.
                </p>
              </div>
              <div className="info-item">
                <div className="info-icon">📍</div>
                <h3>Location</h3>
                <p>Venue details to plan setup and logistics efficiently.</p>
              </div>
              <div className="info-item">
                <div className="info-icon">💰</div>
                <h3>Budget</h3>
                <p>Your budget range to provide options that fit your needs.</p>
              </div>
            </div>
          </div>

          <form className="quote-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="eventType">Event Type *</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Event Type</option>
                  <option value="private-party">Private Party</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="wedding">Wedding</option>
                  <option value="large-event">Large Event (50+ guests)</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="eventDate">Event Date *</label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="guestCount">Number of Guests *</label>
                <input
                  type="number"
                  id="guestCount"
                  name="guestCount"
                  value={formData.guestCount}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="location">Event Location *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Address or venue name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="budget">Budget Range</label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
              >
                <option value="">Select Budget Range</option>
                <option value="under-1000">Under $1,000</option>
                <option value="1000-2500">$1,000 - $2,500</option>
                <option value="2500-5000">$2,500 - $5,000</option>
                <option value="over-5000">Over $5,000</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="specialRequests">Special Requests or Notes</label>
              <textarea
                id="specialRequests"
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows="4"
                placeholder="Any specific cocktails, dietary restrictions, or special requirements..."
              />
            </div>

            <button
              type="submit"
              className={`btn btn-primary btn-large ${
                isSubmitting ? "loading" : ""
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Request Quote"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Quote;
